import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";

const SESSION_KEY = "fabj_session_id";
const getSessionId = () => {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random();
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
};

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const sendDwell = (path, session_id, duration_ms) => {
  if (!duration_ms || duration_ms < 1500) return; // ignore bounces < 1.5s
  const body = JSON.stringify({ path, session_id, duration_ms });
  // Prefer sendBeacon so it survives page unload
  const url = `${API_BASE}/api/track/dwell`;
  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: "application/json" });
    navigator.sendBeacon(url, blob);
  } else {
    api.post("/track/dwell", { path, session_id, duration_ms }).catch(() => {});
  }
};

export const usePageTracking = () => {
  const location = useLocation();
  const enterAt = useRef(Date.now());
  const currentPath = useRef(location.pathname);

  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;
    // Send dwell for the PREVIOUS path before overwriting
    const prev = currentPath.current;
    if (prev && prev !== location.pathname) {
      sendDwell(prev, getSessionId(), Date.now() - enterAt.current);
    }
    currentPath.current = location.pathname;
    enterAt.current = Date.now();

    api.post("/track/visit", {
      path: location.pathname,
      session_id: getSessionId(),
      referrer: document.referrer || "",
    }).catch(() => {});
  }, [location.pathname]);

  // Fire dwell on unload / visibility hidden
  useEffect(() => {
    const flush = () => {
      if (currentPath.current && !currentPath.current.startsWith("/admin")) {
        sendDwell(currentPath.current, getSessionId(), Date.now() - enterAt.current);
      }
    };
    const onVis = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);
};
